<script setup>
import { useStore } from "vuex";
import { useLoading } from "vue-loading-overlay";
import { computed, onMounted, ref } from "vue";
import moment from "moment";
import maintenanceApi from "@/api/maintenance";
import { errorNotify, successNotify } from "@/mixins";
import DataTable from "@/components/panels/DataTable.vue";
import FormModal from "@/components/modals/FormModal.vue";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import PaginationBar from "@/components/layouts/PaginationBar.vue";

const store = useStore();
const $loading = useLoading({});

const taskList = computed(() => store.getters['maintenance/taskList']);
const deviceList = computed(() => store.getters['locker/lockerList']);
const userList = computed(() => store.getters['user/userList']);
const tasks = computed(() => taskList.value?.results || []);

const page = ref(1);
const status = ref('');
const type = ref('');
const device = ref('');

const TYPE_META = {
  maintenance: { cls: 'planned', icon: 'calendar-check-outline', label: 'Planned' },
  repair: { cls: 'unplanned', icon: 'alert-decagram-outline', label: 'Unplanned' },
};

const STATUS_META = {
  scheduled: { cls: 'mt-open', label: 'Scheduled' },
  assigned: { cls: 'mt-assigned', label: 'Assigned' },
  in_progress: { cls: 'mt-progress', label: 'In Progress' },
  done: { cls: 'mt-done', label: 'Done' },
  cancelled: { cls: 'mt-cancelled', label: 'Cancelled' },
};

const OPEN_STATUSES = ['scheduled', 'assigned', 'in_progress'];

function getFilteredTaskList() {
  const loader = $loading.show({});
  let filter = `?page=${page.value}`;
  if (status.value) filter += `&status=${status.value}`;
  if (type.value) filter += `&type=${type.value}`;
  if (device.value) filter += `&device=${device.value}`;
  return store.dispatch('maintenance/getTaskList', filter)
    .finally(() => loader.hide());
}

function applyFilters() {
  page.value = 1;
  getFilteredTaskList();
}

// status KPI counters
const statusCounts = ref({ scheduled: 0, assigned: 0, in_progress: 0, done: 0 });
const totalTasks = ref(0);

function loadCounts() {
  maintenanceApi.getTaskList('?page_size=1')
    .then((r) => { totalTasks.value = r.data.count ?? 0; })
    .catch(() => {});
  Object.keys(statusCounts.value).forEach((st) => {
    maintenanceApi.getTaskList(`?status=${st}&page_size=1`)
      .then((r) => { statusCounts.value[st] = r.data.count ?? 0; })
      .catch(() => {});
  });
}

function reload() {
  getFilteredTaskList();
  loadCounts();
}

onMounted(() => {
  reload();
  store.dispatch('locker/getLockerList', '?page_size=1000');
  store.dispatch('user/getUserList', '?page_size=1000');
});

const metrics = computed(() => [
  { label: 'Scheduled', value: statusCounts.value.scheduled, icon: 'calendar-clock-outline', color: 'var(--lb-blue)' },
  { label: 'Assigned', value: statusCounts.value.assigned, icon: 'account-arrow-right-outline', color: 'var(--lb-warning)' },
  { label: 'In Progress', value: statusCounts.value.in_progress, icon: 'progress-wrench', color: 'var(--lb-info)' },
  { label: 'Done', value: statusCounts.value.done, icon: 'check-circle-outline', color: 'var(--lb-success)' },
]);

// TODO(avg time to close / requests by type): no backend endpoint for this
// statistic yet — demo data for now, swap for real data once the API exists.
const avgTimeToClose = { value: '1.6', unit: 'd', foot: 'Avg resolution time · 30 days' };
const kindDemo = [
  { label: 'Planned', v: 8, color: 'var(--lb-blue)' },
  { label: 'Unplanned', v: 8, color: 'var(--lb-red)' },
];
const kindMax = Math.max(...kindDemo.map((d) => d.v), 1);
const kindTotal = kindDemo.reduce((s, d) => s + d.v, 0);

const initials = (name) =>
  String(name || '').split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();

const fmtDate = (d) => (d ? moment(d).format('MMM D, YYYY') : '—');

// ---- create / edit ----
const modal = ref(null); // { mode: 'add'|'edit', data }
const submitting = ref(false);

const taskFields = computed(() => {
  const fields = [
    { key: 'title', label: 'Title', placeholder: 'Quarterly checkup', required: true, full: true },
    {
      key: 'device', label: 'Device', type: 'select', required: true, placeholder: 'Select device',
      options: (deviceList.value?.results || []).map((d) => ({ value: d.sn, label: d.name })),
    },
    {
      key: 'type', label: 'Type', type: 'select', required: true, placeholder: 'Select type',
      options: [
        { value: 'maintenance', label: 'Planned maintenance' },
        { value: 'repair', label: 'Unplanned repair' },
      ],
    },
    { key: 'scheduled_for', label: 'Scheduled for', type: 'date', required: true },
    {
      key: 'assignee', label: 'Assignee', type: 'select', optional: true, placeholder: 'Unassigned',
      options: (userList.value?.results || []).map((u) => ({ value: String(u.id), label: u.first_name || u.email })),
    },
    { key: 'description', label: 'Description', type: 'textarea', optional: true, full: true, placeholder: 'Breakdown description (required for repairs)' },
  ];
  if (modal.value?.mode === 'edit') {
    // device/type/assignee change via separate API actions when editing
    return fields.filter((f) => ['title', 'scheduled_for', 'description'].includes(f.key));
  }
  return fields;
});

function openModal(mode, task) {
  modal.value = {
    mode,
    data: mode === 'edit'
      ? { id: task.id, title: task.title, scheduled_for: task.scheduled_for, description: task.description || '' }
      : {},
  };
}

function saveTask(vals) {
  submitting.value = true;
  let req;
  if (modal.value.mode === 'add') {
    const data = {
      title: vals.title, device: vals.device, type: vals.type,
      scheduled_for: vals.scheduled_for, description: vals.description || '',
    };
    if (vals.assignee) data.assignee = Number(vals.assignee);
    req = store.dispatch('maintenance/createTask', data);
  } else {
    req = store.dispatch('maintenance/updateTask', {
      id: modal.value.data.id,
      data: { title: vals.title, scheduled_for: vals.scheduled_for, description: vals.description || '' },
    });
  }
  req
    .then(() => {
      const err = store.getters['maintenance/taskError'];
      if (err) {
        errorNotify(err);
      } else {
        successNotify(modal.value.mode === 'add' ? 'Task created' : 'Task updated');
        modal.value = null;
        reload();
      }
    })
    .finally(() => { submitting.value = false; });
}

// ---- assign ----
const assignModal = ref(null); // task
const assignFields = computed(() => [
  {
    key: 'assignee', label: 'Assignee', type: 'select', required: true, full: true, placeholder: 'Select employee',
    options: (userList.value?.results || []).map((u) => ({ value: String(u.id), label: u.first_name || u.email })),
  },
]);

function saveAssign(vals) {
  store.dispatch('maintenance/assignTask', { id: assignModal.value.id, assignee: Number(vals.assignee) })
    .then(() => {
      const err = store.getters['maintenance/taskError'];
      if (err) {
        errorNotify(err);
      } else {
        successNotify('Task assigned');
        assignModal.value = null;
        reload();
      }
    });
}

// ---- complete / cancel / delete ----
const completeModal = ref(null); // task
const completeFields = [
  { key: 'report', label: 'Report', type: 'textarea', required: true, full: true, placeholder: 'What was done…' },
];

function saveComplete(vals) {
  store.dispatch('maintenance/completeTask', { id: completeModal.value.id, report: vals.report })
    .then(() => {
      const err = store.getters['maintenance/taskError'];
      if (err) {
        errorNotify(err);
      } else {
        successNotify('Task completed');
        completeModal.value = null;
        reload();
      }
    });
}

const cancelTaskRef = ref(null);
function confirmCancel() {
  store.dispatch('maintenance/cancelTask', { id: cancelTaskRef.value.id, reason: '' })
    .then(() => {
      const err = store.getters['maintenance/taskError'];
      if (err) errorNotify(err);
      else successNotify('Task cancelled');
      cancelTaskRef.value = null;
      reload();
    });
}

const del = ref(null);
function confirmDelete() {
  store.dispatch('maintenance/deleteTask', del.value.id)
    .then(() => {
      const err = store.getters['maintenance/taskError'];
      if (err) errorNotify(err);
      else successNotify('Task deleted');
      del.value = null;
      reload();
    });
}

const cols = 'minmax(200px,1.5fr) minmax(140px,1fr) 130px 130px minmax(160px,1fr) 130px';
const columns = ['Task', 'Device', 'Type', 'Status', 'Assignee', 'Scheduled'];
</script>

<template>
  <Teleport to="#topbar-actions">
    <button class="btn-primary" type="button" @click="openModal('add')">
      <span class="mdi mdi-plus"></span>
      New Task
    </button>
  </Teleport>

  <!-- metrics -->
  <div class="ro-top">
    <div class="metric-cluster">
      <div class="mc-total">
        <span class="mc-total__wm mdi mdi-wrench-outline"></span>
        <div class="mc-total__title">Total Tasks</div>
        <div class="mc-total__num">{{ totalTasks.toLocaleString() }}</div>
      </div>
      <div v-for="m in metrics" :key="m.label" class="mcard" :class="{ 'is-zero': m.value === 0 }">
        <div class="mcard__l">
          <div class="mcard__ico"><span class="mdi" :class="`mdi-${m.icon}`" :style="{ color: m.color }"></span></div>
          <div class="mcard__label">{{ m.label }}</div>
        </div>
        <div class="mcard__num">{{ m.value }}</div>
      </div>
    </div>

    <div class="ro-right">
      <div class="ro-card avgcard">
        <span class="avgcard__wm mdi mdi-timer-sand"></span>
        <div class="avgcard__title">Avg. Time to Close</div>
        <div class="avgcard__num">{{ avgTimeToClose.value }}<small>{{ avgTimeToClose.unit }}</small></div>
        <div class="avgcard__foot">{{ avgTimeToClose.foot }}</div>
      </div>
      <div class="ro-card">
        <div class="ro-card__head">
          <span class="mdi mdi-chart-bar"></span>
          <h3>Requests by Type</h3>
          <span class="sub">{{ kindTotal }} total</span>
        </div>
        <div class="weekchart">
          <div v-for="d in kindDemo" :key="d.label" class="weekbar">
            <div class="weekbar__val">{{ d.v }}</div>
            <div class="weekbar__track">
              <div class="weekbar__fill" :style="{ height: `${(d.v / kindMax) * 100}%`, background: d.color }"></div>
            </div>
            <div class="weekbar__day">{{ d.label }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- toolbar -->
  <div class="toolbar">
    <div class="field-select">
      <span class="mdi mdi-tag-outline lead"></span>
      <select v-model="status" @change="applyFilters">
        <option value="">All statuses</option>
        <option v-for="(meta, key) in STATUS_META" :key="key" :value="key">{{ meta.label }}</option>
      </select>
      <span class="mdi mdi-chevron-down caret"></span>
    </div>
    <div class="field-select">
      <span class="mdi mdi-wrench-outline lead"></span>
      <select v-model="type" @change="applyFilters">
        <option value="">All types</option>
        <option value="maintenance">Planned</option>
        <option value="repair">Unplanned</option>
      </select>
      <span class="mdi mdi-chevron-down caret"></span>
    </div>
    <div v-if="deviceList" class="field-select">
      <span class="mdi mdi-locker lead"></span>
      <select v-model="device" @change="applyFilters">
        <option value="">All devices</option>
        <option v-for="d in deviceList.results || []" :key="d.sn" :value="d.sn">{{ d.name }}</option>
      </select>
      <span class="mdi mdi-chevron-down caret"></span>
    </div>
    <div class="spacer"></div>
    <span class="count">{{ taskList?.count ?? 0 }} tasks</span>
  </div>

  <!-- table -->
  <DataTable :cols="cols" :columns="columns" :rows="tasks" empty-text="No tasks match your filters.">
    <template #row="{ row }">
      <div class="td">
        <div class="td-name">
          <div class="ic"><span class="mdi mdi-wrench-outline"></span></div>
          <div class="nm">
            <b>{{ row.title }}</b>
            <small>{{ String(row.id).split('-')[0].toUpperCase() }}</small>
          </div>
        </div>
      </div>
      <div class="td"><span>{{ row.device?.name || '—' }}</span></div>
      <div class="td">
        <span class="kindchip" :class="`kindchip--${(TYPE_META[row.type] || TYPE_META.maintenance).cls}`">
          <span class="mdi" :class="`mdi-${(TYPE_META[row.type] || TYPE_META.maintenance).icon}`"></span>
          {{ (TYPE_META[row.type] || { label: row.type }).label }}
        </span>
      </div>
      <div class="td">
        <span class="pill" :class="`pill--${(STATUS_META[row.status] || STATUS_META.scheduled).cls}`">
          <span class="d"></span>
          {{ (STATUS_META[row.status] || { label: row.status }).label }}
        </span>
      </div>
      <div class="td">
        <span v-if="row.assignee" class="assignee">
          <span class="assignee__av">{{ initials(row.assignee.first_name || row.assignee.email) }}</span>
          <span class="assignee__name">{{ row.assignee.first_name || row.assignee.email }}</span>
        </span>
        <span v-else class="assignee unassigned">
          <span class="mdi mdi-account-question-outline"></span>&nbsp;Unassigned
        </span>
      </div>
      <div class="td"><span class="muted">{{ fmtDate(row.scheduled_for) }}</span></div>

      <div class="actionbar">
        <template v-if="OPEN_STATUSES.includes(row.status)">
          <button class="ab" title="Assign" type="button" @click.stop="assignModal = row">
            <span class="mdi mdi-account-arrow-right-outline"></span>
          </button>
          <button class="ab" title="Complete" type="button" @click.stop="completeModal = row">
            <span class="mdi mdi-check"></span>
          </button>
          <button class="ab" title="Cancel task" type="button" @click.stop="cancelTaskRef = row">
            <span class="mdi mdi-cancel"></span>
          </button>
          <button class="ab-detail" type="button" @click.stop="openModal('edit', row)">
            Edit <span class="mdi mdi-arrow-right"></span>
          </button>
        </template>
        <button class="ab ab-danger" title="Delete" type="button" @click.stop="del = row">
          <span class="mdi mdi-trash-can-outline"></span>
        </button>
      </div>
    </template>
  </DataTable>

  <PaginationBar
    :page="page"
    :total-pages="taskList?.total_pages || 1"
    :count="taskList?.count || 0"
    @update:page="(p) => { page = p; getFilteredTaskList(); }" />

  <!-- modals -->
  <FormModal
    :open="!!modal"
    :title="modal?.mode === 'edit' ? 'Edit Task' : 'New Maintenance Task'"
    :fields="taskFields"
    :initial="modal?.data || {}"
    :submit-label="modal?.mode === 'edit' ? 'Save changes' : 'Create task'"
    :submitting="submitting"
    @submit="saveTask"
    @cancel="modal = null" />

  <FormModal
    :open="!!assignModal"
    title="Assign Task"
    :fields="assignFields"
    :initial="{ assignee: assignModal?.assignee ? String(assignModal.assignee.id) : '' }"
    submit-label="Assign"
    @submit="saveAssign"
    @cancel="assignModal = null" />

  <FormModal
    :open="!!completeModal"
    title="Complete Task"
    :fields="completeFields"
    :initial="{}"
    submit-label="Complete"
    @submit="saveComplete"
    @cancel="completeModal = null" />

  <ConfirmModal
    :open="!!cancelTaskRef"
    danger
    title="Cancel task?"
    :message="cancelTaskRef ? `“${cancelTaskRef.title}” will be cancelled.` : ''"
    confirm-label="Cancel task"
    @confirm="confirmCancel"
    @cancel="cancelTaskRef = null" />

  <ConfirmModal
    :open="!!del"
    danger
    title="Delete task?"
    :message="del ? `“${del.title}” will be permanently removed.` : ''"
    confirm-label="Delete"
    @confirm="confirmDelete"
    @cancel="del = null" />
</template>
